import { AddButton, IconButton, Paragraph, Tooltip } from "@smartsuite/react-ui/lib";
import { useMemo } from "react";
import { createSharedFiles } from "../../api";
import { maximumUploadFiles, maximumUploadSize } from "../../config/env";
import { createFilestackClient } from "../../config/filestack";
import { useFormContext } from "../../hooks/useFormContext";
import { SharedFile, SharedFileInput } from "../../types/file";
import { $t } from "../../utils/intl";
import "./FileUpload.sass";
import { fileUploadText } from "./FileUpload.text";
import { getFileCdnUrl } from "./helpers";
import { useIsPreview } from "../../hooks/useIsPreview";

export interface FileUploadProps {
  files: SharedFile[];
  maxUploadFiles?: number;
  maxUploadSize?: number;
  readOnly?: boolean;
  onChange: (files: SharedFile[]) => void;
}

const FileUpload: React.FunctionComponent<FileUploadProps> = ({
  files = [],
  maxUploadFiles = maximumUploadFiles,
  maxUploadSize = maximumUploadSize,
  readOnly,
  onChange,
}) => {
  const { accountId, config } = useFormContext();

  const { isPreview } = useIsPreview();

  const filestack = useMemo(
    () => createFilestackClient(config.filestack_api_key, { security: config.file_security }),
    [config.file_security, config.filestack_api_key]
  );

  const handleAdd = async (): Promise<void> => {
    const picker = filestack.picker({
      maxSize: maxUploadSize,
      maxFiles: maxUploadFiles,
      onUploadDone: (response) => {
        const sharedFiles: SharedFileInput[] = response.filesUploaded.map((file) => ({
          handle: file.handle,
          metadata: {
            container: file.container ?? "",
            filename: file.filename,
            key: file.key ?? "",
            mimetype: file.mimetype ?? "unknown",
            size: file.size,
          },
        }));

        if (sharedFiles.length > 0 && config.sharing_hash) {
          createSharedFiles(accountId, config.sharing_hash, sharedFiles, isPreview)
            .then((result) => {
              const updatedFiles = [...files, ...result];
              onChange(updatedFiles);
            })
            .catch(console.error);
        }
      },
    });

    void picker.open();
  };

  const handleRemove = (removeFile: SharedFile) => () => {
    onChange(files.filter((file) => file !== removeFile));
  };

  return (
    <div className="file-upload">
      <ul className="file-upload__list">
        {files.map((file) => (
          <li key={file.handle} className="file-upload__list-item">
            <img
              src={`/images/filetypes/${file.icon}.svg`}
              alt={file.icon}
              width={20}
              height={20}
            />

            <Paragraph color="link" weight="medium">
              <a href={getFileCdnUrl(filestack, file)} target="_blank" rel="noreferrer">
                {file.metadata.filename}
              </a>
            </Paragraph>

            <Tooltip
              text="Remove"
              placements={["top"]}
              padding={2}
              target={
                <IconButton name="close" size="S" ariaLabel="Remove" onClick={handleRemove(file)} />
              }
            />
          </li>
        ))}
      </ul>

      {!readOnly && (
        <AddButton
          textStyle={{ size: "s" }}
          iconStyle={{ size: "S" }}
          onClick={() => {
            void handleAdd();
          }}
        >
          {$t(fileUploadText.addAttachmentText)}
        </AddButton>
      )}
    </div>
  );
};

export default FileUpload;
